import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { APP } from '@/constants/testIds';
import { LayoutDashboard, Radar, Network, AlertTriangle, FileText, Settings as SettingsIcon, LifeBuoy, Shield, LogOut, Menu, X, ShieldAlert, FileSearch } from 'lucide-react';

const NAV_CUSTOMER = [
  { to: '/app', label: 'Overview', icon: LayoutDashboard, end: true, testId: APP.navOverview },
  { to: '/app/scan', label: 'Network Scan', icon: Radar, testId: APP.navScan },
  { to: '/app/ip-tracker', label: 'IP Tracker', icon: Network, testId: APP.navIpTracker },
  { to: '/app/login-attempts', label: 'Login Attempts', icon: ShieldAlert, testId: 'nav-login-attempts' },
  { to: '/app/forensic-reports', label: 'Forensic Reports', icon: FileSearch, testId: 'nav-forensic-reports' },
  { to: '/app/alerts', label: 'Alerts', icon: AlertTriangle, testId: APP.navAlerts },
  { to: '/app/reports', label: 'Reports', icon: FileText, testId: APP.navReports },
  { to: '/app/settings', label: 'Settings', icon: SettingsIcon, testId: APP.navSettings },
  { to: '/app/support', label: 'Support', icon: LifeBuoy, testId: APP.navSupport },
];

const SidebarContent = ({ user, isAdmin, onLinkClick, onCloseDrawer, onLogout }) => (
  <>
    <div className="p-5 md:p-6 border-b border-[color:var(--border)] flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3" onClick={onLinkClick}>
        <span className="logo-mark">S</span>
        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase">Supreme</div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--brand)]">Web Security</div>
        </div>
      </Link>
      {onCloseDrawer && (
        <button
          className="md:hidden p-2 -mr-2 text-[color:var(--text-secondary)] hover:text-white"
          onClick={onCloseDrawer}
          data-testid="mobile-drawer-close"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>
      )}
    </div>
    <nav className="p-3 md:p-4 space-y-1 flex-1 overflow-y-auto">
      <div className="label-tag px-3 mb-3">// Customer Portal</div>
      {NAV_CUSTOMER.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={it.testId}
          onClick={onLinkClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm font-mono tracking-wide transition-colors border ${
              isActive
                ? 'border-[color:var(--brand)] text-[color:var(--brand)] bg-[color:var(--brand)]/5'
                : 'border-transparent text-[color:var(--text-secondary)] hover:text-white hover:bg-[color:var(--bg-surface)]'
            }`
          }
        >
          <it.icon size={15} />
          <span className="uppercase tracking-[0.15em] text-[11px]">{it.label}</span>
        </NavLink>
      ))}
      {isAdmin && (
        <>
          <div className="label-tag px-3 mb-3 mt-6">// Admin</div>
          <NavLink
            to="/admin"
            data-testid={APP.navAdmin}
            onClick={onLinkClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-sm font-mono tracking-wide transition-colors border ${
                isActive
                  ? 'border-[color:var(--brand)] text-[color:var(--brand)] bg-[color:var(--brand)]/5'
                  : 'border-transparent text-[color:var(--text-secondary)] hover:text-white hover:bg-[color:var(--bg-surface)]'
              }`
            }
          >
            <Shield size={15} />
            <span className="uppercase tracking-[0.15em] text-[11px]">Admin Dashboard</span>
          </NavLink>
        </>
      )}
    </nav>
    <div className="p-4 border-t border-[color:var(--border)]">
      <div className="flex items-center gap-3 mb-3">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 border border-[color:var(--border)]" />
        ) : (
          <div className="w-8 h-8 border border-[color:var(--border)] flex items-center justify-center font-mono text-xs">
            {user?.name?.[0] || '?'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs truncate">{user?.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] truncate">
            {user?.role} · {user?.package === 'none' ? 'No plan' : user?.package}
          </div>
        </div>
      </div>
      {user?.role === 'customer' && (
        <div className="mb-3 panel-soft px-3 py-2 flex items-center justify-between" data-testid="sidebar-credits">
          <span className="label-tag">Credits</span>
          <span className={`font-mono text-sm ${user?.credits > 0 ? 'text-[color:var(--brand)]' : 'text-[color:var(--status-medium)]'}`}>
            {(user?.credits ?? 0).toLocaleString()}
          </span>
        </div>
      )}
      <button
        data-testid={APP.logoutBtn}
        onClick={onLogout}
        className="btn-secondary w-full justify-center !py-2 !text-[10px]"
      >
        <LogOut size={12} /> Sign out
      </button>
    </div>
  </>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === 'admin';
  const warnedRef = useRef(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // One-time-per-session low-balance warning on sign-in
  useEffect(() => {
    if (warnedRef.current) return;
    if (!user || user.role !== 'customer') return;
    if (user.payment_status === 'paid') return;
    const credits = Number(user.credits ?? 0);
    if (credits <= 0 || credits >= 200) return;
    if (sessionStorage.getItem('low_credits_warned') === user.user_id) return;
    warnedRef.current = true;
    sessionStorage.setItem('low_credits_warned', user.user_id);
    const left = Math.floor(credits / 100);
    toast.warning(`Heads up — only ${left} scan${left === 1 ? '' : 's'} left (${credits} credits).`, {
      duration: 6000,
      description: 'Add credits or upgrade to keep scanning.',
    });
  }, [user]);

  const currentLabel = NAV_CUSTOMER.find(
    (n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))
  )?.label || 'Console';

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen flex bg-[color:var(--bg-base)]">
      {/* Desktop sidebar */}
      <aside
        data-testid={APP.sidebar}
        className="hidden md:flex w-64 shrink-0 border-r border-[color:var(--border)] flex-col h-screen sticky top-0"
      >
        <SidebarContent user={user} isAdmin={isAdmin} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            data-testid="mobile-drawer-backdrop"
          />
          <aside className="relative w-72 max-w-[85vw] h-full bg-[color:var(--bg-base)] border-r border-[color:var(--border)] flex flex-col">
            <SidebarContent
              user={user}
              isAdmin={isAdmin}
              onLogout={handleLogout}
              onLinkClick={() => setOpen(false)}
              onCloseDrawer={() => setOpen(false)}
            />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="md:hidden sticky top-0 z-30 border-b border-[color:var(--border)] bg-[color:var(--bg-base)]/95 backdrop-blur flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 -ml-2 border border-[color:var(--border)] text-[color:var(--text-secondary)] hover:border-[color:var(--brand)] hover:text-[color:var(--brand)]"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
            <span className="logo-mark mr-1" style={{ width: 22, height: 22, fontSize: 11 }}>S</span>
            <span className="text-[color:var(--text-secondary)]">{currentLabel}</span>
          </div>
          <button
            onClick={handleLogout}
            data-testid="mobile-logout-btn"
            className="p-2 -mr-2 text-[color:var(--text-secondary)] hover:text-white"
            aria-label="Sign out"
          >
            <LogOut size={16} />
          </button>
        </header>

        <main className="flex-1 min-w-0">
          <div className="max-w-[1400px] mx-auto p-4 sm:p-6 md:p-10">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
